import { useNavigate } from 'react-router';
import styles from '~/styles/bookingFlow/steps.module.css';
import cnh from '~/helpers/cnh';
import useTranslation from '~/hooks/useTranslation';
import bookingFlowRoutes from '~/routes/bookingFlow';
import { useOptions } from '~/hooks/useOptions';
import { InferredBookingFlowOptions } from '~/types/options';
import { useCallback } from 'react';

type Props = {
  step: number;
};

type Step = {
  step: number;
  label: string;
  route?: string;
  href?: string;
};

export default function Steps({ step }: Props) {
  const translate = useTranslation();
  const navigate = useNavigate();
  const options = useOptions() as InferredBookingFlowOptions;

  const steps: Step[] = [
    {
      step: 0,
      label: translate('bookingFlow.steps.my_trip'),
      route: bookingFlowRoutes.OVERVIEW,
    },
    {
      step: 1,
      label: translate('bookingFlow.steps.personal_information'),
      route: bookingFlowRoutes.INFORMATION,
    },
    {
      step: 2,
      label: translate('bookingFlow.steps.summary'),
      route: bookingFlowRoutes.SUMMARY,
    },
  ];

  if (options.calendarStepUrl) {
    steps.unshift({
      step: -1,
      label: translate('bookingFlow.steps.calendar'),
      href: options.calendarStepUrl,
    });
  }

  const renderStep = useCallback((stepItem: Step, stepIndex: number) => {
    const isCurrentStep = step === stepItem.step;
    const isDisabled = step < stepItem.step;

    if (stepItem.href) {
      return (
        <a
          key={stepItem.label}
          href={stepItem.href}
          className={cnh(
            styles.stepsStep,
            isCurrentStep ? styles.stepsStepActive : '',
          )}
        >
          <span className={styles.stepsStepNumber}>
            {stepIndex + 1}
          </span>
          <span className={styles.stepsStepLabel}>
            {stepItem.label}
          </span>
        </a>
      );
    }

    if (stepItem.route) {
      return (
        <button
          key={stepItem.label}
          type="button"
          onClick={() => {
            navigate(stepItem.route ?? '');
          }}
          disabled={isDisabled}
          className={cnh(
            styles.stepsStep,
            isCurrentStep ? styles.stepsStepActive : '',
          )}
        >
          <span className={styles.stepsStepNumber}>
            {stepIndex + 1}
          </span>
          <span className={styles.stepsStepLabel}>
            {stepItem.label}
          </span>
        </button>
      );
    }

    throw new Error('Step should either have a route or an href');
  }, [navigate, step]);

  // On the last step, the steps component is not shown.
  if (step >= 3) {
    return null;
  }

  return (
    <div className={styles.steps}>
      {steps.map((stepItem, stepIndex) => {
        return renderStep(stepItem, stepIndex);
      })}
    </div>
  );
}
